/* eslint-disable import/prefer-default-export */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';
import Peripherals from '@appblocks/codegeneration/Peripherals';


export const getCodeBlocks = (feature: BaseFeature): CodeBlock[] => {
    const blocks: Array<CodeBlock> = [];
    const NAME = feature.getSettingValue('NAME');
    const PIN = feature.getSettingValue('PIN');
    const DIRECTION = feature.getSettingValue('DIRECTION');
    const STATE = feature.getSettingValue('STATE');
    const PULL = feature.getSettingValue('PULL');
    const dtsName = `${NAME}_gpio`;
    const ID = feature.getID();

    const peripherals = Peripherals.getPin(PIN, feature.project.device.id)?.peripherals;
    let peripheralPort = '0';
    let gpioNum: string | undefined = '0';
    if (peripherals) {
        peripheralPort = peripherals.filter(p => p.peripheralType === 'gpio')[0].peripheralPort;
        gpioNum = peripherals.filter(p => p.peripheralType === 'gpio')[0].num;
    }

    blocks.push({
        location: 'GLOBAL_INCLUDES',
        blockType: BlockType.CODE,
        id: '#include <zephyr/drivers/gpio.h>',
        template: '#include <zephyr/drivers/gpio.h>',
    });

    blocks.push({
        location: 'PRJ_CONF',
        blockType: BlockType.CODE,
        id: 'CONFIG_GPIO=y',
        template: 'CONFIG_GPIO=y',
    });

    blocks.push({
        location: 'APP_OVERLAY_DTS',
        blockType: BlockType.CODE,
        id: 'zephyr_app_overlay_outputs',
        template: `
gpio_custom_outputs {
    compatible = "gpio-leds";
    /****zephyr_app_overlay_outputs****/
};`,
    });

    blocks.push({
        location: 'APP_OVERLAY_DTS',
        blockType: BlockType.CODE,
        id: 'zephyr_app_overlay_inputs',
        template: `
gpio_custom_inputs {
    compatible = "gpio-keys";
    /****zephyr_app_overlay_inputs****/
};`,
    });

    let pullCode = '';
    if (PULL === 'up') {
        pullCode = ' | GPIO_PULL_UP';
    } else if (PULL === 'down') {
        pullCode = ' | GPIO_PULL_DOWN';
    }

    const gpioFeature: any = feature;

    const dtsNode = `
    ${dtsName}:${dtsName} {
        gpios = <&${peripheralPort} ${gpioNum} ${gpioFeature.getFlags()}>;
    };
    `;

    // see if any feature is referencing this GPIO
    // dont add code if its controlled by another peripheral
    const features = feature.project.features;
    let found = false;
    for (let i = 0; i < features.length; i++) {
        if (features[i].id === 'gpio') {
            continue;
        }
        const settings = features[i].settings;
        for (let j = 0; j < settings.length; j++) {
            if (settings[j].value === ID) {
                found = true;
                break;
            }
        }
    }

    if (found) {
        return blocks;
    }

    if (DIRECTION === '1') {
        // Input

        blocks.push({
            location: 'boot',
            blockType: BlockType.CODE,
            template: `
gpio_pin_configure_dt(&${dtsName}, GPIO_INPUT);

if (gpio_pin_interrupt_configure_dt(&${dtsName}, GPIO_INT_EDGE_TO_ACTIVE) != 0) {
    printk("failed to configure interrupt on %s pin %d\\n", ${dtsName}.port->name, ${dtsName}.pin);
    return 0;
}
gpio_init_callback(&${dtsName}_cb_data, ${dtsName}_pressed, BIT(${dtsName}.pin));
gpio_add_callback(${dtsName}.port, &${dtsName}_cb_data);
    `,
        });

        blocks.push({
            location: 'zephyr_app_overlay_inputs',
            blockType: BlockType.CODE,
            template: dtsNode,
        });

        blocks.push({
            location: 'FUNCTION_DECLARATIONS',
            blockType: BlockType.CODE,
            template: `

void ${dtsName}_pressed(const struct device *dev, struct gpio_callback *cb, uint32_t pins) {
    
}`,
        });

        blocks.push({
            location: 'VARIABLE_DECLARATIONS',
            blockType: BlockType.CODE,
            template: `
static struct gpio_callback ${dtsName}_cb_data;`,
        });

        blocks.push({
            location: `var_${NAME}_update`,
            blockType: BlockType.CODE,
            template: `
var_${NAME}_update_completed(gpio_pin_get_dt(&${dtsName}));`,
        });


        // blocks.push({
        //     location: `var_${NAME}_update`,
        //     blockType: BlockType.CODE,
        //     template: `var_${NAME}_set(${NAME}_pin.value())`,
        // });
    } else if (DIRECTION === '0') {
        // output
        // blocks.push({
        //     location: 'VARIABLE_DECLARATIONS',
        //     blockType: BlockType.CODE,
        //     template: `${NAME}_pin = machine.Pin(${gpioNum}, machine.Pin.OUT)`,
        // });

        blocks.push({
            location: 'boot',
            blockType: BlockType.CODE,
            template: `gpio_pin_configure_dt(&${dtsName}, GPIO_OUTPUT_ACTIVE);`,
        });
        blocks.push({
            location: 'zephyr_app_overlay_outputs',
            blockType: BlockType.CODE,
            template: dtsNode,
        });

        blocks.push({
            location: `var_${NAME}_set`,
            blockType: BlockType.CODE,
            template: `gpio_pin_set_dt(&${dtsName}, value);`,
        });
    }

    if (DIRECTION !== '') {
        blocks.push({
            location: 'GLOBAL_DEFINES',
            blockType: BlockType.CODE,
            template: `
#if !DT_NODE_EXISTS(DT_NODELABEL(${dtsName}))
#error "Overlay for ${dtsName} node not properly defined."
#endif`,
        });

        blocks.push({
            location: 'VARIABLE_DECLARATIONS',
            blockType: BlockType.CODE,
            template: `
    static const struct gpio_dt_spec ${dtsName} =
        GPIO_DT_SPEC_GET_OR(DT_NODELABEL(${dtsName}), gpios, {0});
            `,
        });
    }

    return blocks;
};
